import { z } from 'zod/v4'

import { WorkflowNodeWaitSchema } from '@features/workflows/features/workflowRunSteps/resources/types'

export const CreateWorkflowRunSchema = z.object({
  workflowId: z.string(),
  workflowVersionId: z.string(),
  inputs: z.record(z.string(), z.unknown()),
  batchJobId: z.string().optional(),
  trigger: z
    .object({
      id: z.string(),
      type: z.enum(['webhook', 'subflow', 'agent', 'event-listener']),
      data: z.record(z.string(), z.unknown()),
    })
    .optional(),
})

export const ContactWorkflowRunSchema = z.object({
  id: z.string(),
  createdAt: z.union([
    z.date().transform((date) => date.toISOString()),
    z.string(),
  ]),
  updatedAt: z.union([
    z.date().transform((date) => date.toISOString()),
    z.string(),
  ]),
  startedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  completedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  workflow: z.object({
    id: z.string(),
    name: z.string(),
  }),
})

export type CreateWorkflowRunType = z.infer<typeof CreateWorkflowRunSchema>

// Create a recursive schema for workflow steps
const GetWorkflowRunStepSchemaBase = z.object({
  id: z.string(),
  name: z.string(),
  meta: z.record(z.string(), z.unknown()),
  handle: z.string(),
  status: z.string(),
  createdAt: z.union([
    z.date().transform((date) => date.toISOString()),
    z.string(),
  ]),
  updatedAt: z.union([
    z.date().transform((date) => date.toISOString()),
    z.string(),
  ]),
  startedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  completedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  startedWaitingAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  scheduledAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  wait: WorkflowNodeWaitSchema.nullable(),
  workflowVersionId: z.string().nullable().optional(),
})

// Define the recursive type first
export type GetWorkflowRunStepType = z.infer<
  typeof GetWorkflowRunStepSchemaBase
> & {
  subflowSteps?: GetWorkflowRunStepType[]
}

// Use lazy to handle recursive schema with explicit typing
export const GetWorkflowRunStepSchema: z.ZodType<GetWorkflowRunStepType> =
  GetWorkflowRunStepSchemaBase.extend({
    // This will be populated for workflow steps that have child workflow steps
    subflowSteps: z.lazy(() => z.array(GetWorkflowRunStepSchema)).optional(),
  }) as z.ZodType<GetWorkflowRunStepType>

export const GetWorkflowRunSchema = CreateWorkflowRunSchema.extend({
  id: z.string(),
  createdAt: z.union([
    z.date().transform((date) => date.toISOString()),
    z.string(),
  ]),
  updatedAt: z.union([
    z.date().transform((date) => date.toISOString()),
    z.string(),
  ]),
  failedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable()
    .optional(),
  cancelledAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  pausedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  startedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  completedAt: z
    .union([z.date().transform((date) => date.toISOString()), z.string()])
    .nullable(),
  steps: z.array(GetWorkflowRunStepSchema),
  workflow: z.object({
    id: z.string(),
    name: z.string(),
  }),
  workflowVersion: z.object({
    id: z.string(),
    version: z.string(),
    versionLabel: z.string(),
  }),
  inputs: z.record(z.string(), z.unknown()),
  trigger: z
    .object({
      info: z.record(z.string(), z.unknown()),
      eventId: z.string(),
    })
    .optional(),
})

export type GetWorkflowRunType = z.infer<typeof GetWorkflowRunSchema>

export {
  WORKFLOW_LIST_ACTIVITY_DAYS,
  WORKFLOW_LIST_HEALTH_RUN_LIMIT,
  WorkflowListActivityPointSchema,
  WorkflowListHealthSchema,
  WorkflowListStatsSchema,
  buildActivityPoints,
  buildEmptyWorkflowListStats,
  buildHealthFromRuns,
  buildWorkflowListStatsByWorkflowId,
  isWorkflowRunSuccessful,
} from './listStats'

export type {
  WorkflowListActivityPoint,
  WorkflowListHealth,
  WorkflowListStats,
  WorkflowRunListStatRow,
} from './listStats'
